<template>
  <PublicLayout variant="no-bg" :hero="false" breadcrumbs-in-content>
    <UContainer>
      <div class="bg-white rounded-[16px] p-8 pb-10 mb-20 max-w-211.5">
        <h1
          class="text-size-h3 leading-9.5 tracking-[-0.004em] font-semibold text-content-default mb-6"
          style="font-family: Inter"
        >
          Политика конфиденциальности
        </h1>
        <div class="flex flex-col gap-5">
          <div v-for="section in sections" :key="section.heading">
            <h2
              class="text-size-h5 leading-h1-paragraph font-bold text-content-default mb-3"
              style="font-family: Lato"
            >
              {{ section.heading }}
            </h2>
            <p
              class="text-base leading-h1-paragraph text-[#3B4047] whitespace-pre-line"
              style="font-family: Lato"
            >
              {{ section.content }}
            </p>
          </div>
        </div>
      </div>
    </UContainer>
  </PublicLayout>
</template>

<script setup lang="ts">
import PublicLayout from '~/layouts/PublicLayout.vue'

defineOptions({
  name: 'PrivacyPolicyPage',
})

definePageMeta({
  breadcrumbs: [
    { name: 'Документы', to: '/documents' },
    { name: 'Политика конфиденциальности' },
  ],
})

useSeo({
  title: 'Политика конфиденциальности',
  description:
    'Политика конфиденциальности платформы «Код Спорта»: порядок обработки и защиты персональных данных пользователей.',
  canonicalPath: '/documents/privacy',
  noindex: true,
})

const sections = [
  {
    heading: '1. Общие положения',
    content: `1.1. Настоящая Политика определяет порядок и условия обработки персональных данных Оператором.
1.2. Термины используются в значении, установленном законодательством РФ о персональных данных.
1.3. Оператор обрабатывает ПДн законно, справедливо, минимально и в пределах заранее определённых целей.
1.4. Соблюдается конфиденциальность (ст. 7), принимаются меры защиты (ч. 2 ст. 18.1, ч. 1 ст. 19).`,
  },
  {
    heading: '2. Основные понятия, используемые в Политике',
    content: `2.1. Автоматизированная обработка персональных данных — обработка персональных данных с помощью средств вычислительной техники.
2.2. Блокирование персональных данных — временное прекращение обработки персональных данных (за исключением случаев, если обработка необходима для уточнения персональных данных).
2.3. Веб-сайт — совокупность материалов и программ, доступных по адресу, указанному в форме (конкретный адрес подставится при генерации).
2.4. Информационная система персональных данных — совокупность баз данных ПДн и технологий/средств их обработки.
2.5. Обезличивание — действия, в результате которых без доп. информации нельзя определить принадлежность ПДн конкретному Пользователю.
2.6. Обработка ПДн — любые действия с ПДн: сбор, запись, систематизация, накопление, хранение, уточнение (обновление, изменение), извлечение, использование, передача (распространение, предоставление, доступ), обезличивание, блокирование, удаление, уничтожение.
2.7. Оператор — лицо, организующее/осуществляющее обработку ПДн и определяющее цели, состав ПДн и операции с ними.
2.8. Персональные данные — любая информация, относящаяся к Пользователю веб-сайта, указанного в форме.
2.9. ПДн, разрешённые для распространения — ПДн, к которым субъект предоставил неограниченный доступ в порядке Закона о ПДн.
2.10. Пользователь — любой посетитель веб-сайта, указанного в форме.
2.11. Предоставление ПДн — раскрытие ПДн определённому лицу/кругу лиц.
2.12. Распространение ПДн — раскрытие ПДн неопределённому кругу лиц (в т.ч. публикация в сети).
2.13. Трансграничная передача ПДн — передача ПДн на территорию иностранного государства.
2.14. Уничтожение ПДн — действия, приводящие к безвозвратному уничтожению ПДн и/или носителей.`,
  },
  {
    heading: '3. Основные права и обязанности Оператора',
    content: `3.1. Оператор имеет право: запрашивать достоверные ПДн; при отзыве согласия продолжить обработку при наличии законных оснований; определять состав мер по выполнению обязанностей по Закону о ПДн.
3.2. Оператор обязан: предоставлять субъекту сведения об обработке; организовывать обработку по закону; отвечать на обращения; сообщать в уполномоченный орган по запросу; публиковать Политику; принимать правовые, организационные и технические меры защиты ПДн; прекращать передачу/обработку и уничтожать ПДн в случаях, предусмотренных законом; исполнять иные обязанности по Закону о ПДн.`,
  },
  {
    heading: '4. Основные права и обязанности субъектов ПДн',
    content: `4.1. Права: получать информацию об обработке; требовать уточнения/блокирования/уничтожения ПДн; выдвигать условие предварительного согласия для продвижения; отзывать согласие и требовать прекращения; обжаловать действия Оператора; иные права по закону.
4.2. Обязанности: предоставлять достоверные данные; сообщать об их уточнении.
4.3. Лица, передавшие недостоверные сведения или данные иных субъектов без согласия, несут ответственность по закону.`,
  },
  {
    heading: '5. Цели сбора и обработки ПДн',
    content:
      '5.1. В генераторе будут перечислены выбранные цели (или добавленные вами вручную).',
  },
  {
    heading: '6. Правовые основания обработки ПДн',
    content:
      '6.1. Под каждую цель будут подставлены соответствующие основания (согласие, договор и т.п.). 152-ФЗ сам по себе не является основанием.',
  },
  {
    heading: '7. Объём и категории ПДн; категории субъектов',
    content:
      '7.1. По целям будут подставлены категории ПДн; субъекты — пользователи/клиенты/контрагенты-физлица, представители юрлиц, работники, кандидаты и др.',
  },
  {
    heading: '8. Порядок и условия обработки ПДн',
    content: `8.1. Обработка осуществляется на основании согласия субъекта (если иное не предусмотрено законом). Согласие подтверждается действиями, направленными на предоставление ПДн (заполнение полей формы, отправка заявки), а также — при наличии на форме — проставлением отметки («галочки») о согласии с Политикой.
8.2. Для каждой цели совершаются операции: сбор, запись, систематизация, накопление, хранение, уточнение, извлечение, использование, передача (предоставление, доступ), обезличивание, блокирование, удаление, уничтожение. Возможна автоматизированная и/или неавтоматизированная обработка. Передача третьим лицам — при наличии оснований; локализация в РФ; cookie/аналитика при необходимости; сроки хранения — не дольше достижения целей либо по закону/договору.`,
  },
  {
    heading: '9. Меры по обеспечению защиты ПДн',
    content: `9.1. Оператор принимает правовые, организационные и технические меры защиты от неправомерного/случайного доступа, уничтожения, изменения, блокирования, копирования, предоставления, распространения и иных неправомерных действий.
9.2. В том числе: назначение ответственных; локальные акты по обработке и защите ПДн; ограничение и регламент доступа сотрудников; применение технических средств защиты; надлежащие условия хранения; внутренний контроль/аудит; ознакомление сотрудников с требованиями закона и локальными актами.`,
  },
  {
    heading:
      '10. Актуализация, исправление, удаление и уничтожение ПДн; ответы на запросы субъектов',
    content:
      '10.1. Актуализация/прекращение при неточности или неправомерности; уничтожение — при достижении целей/отзыве согласия/истечении сроков/выявлении нарушений; ответы на запросы — до 30 календарных дней.',
  },
  {
    heading: '11. Обновление Политики',
    content:
      '11.1. Пересмотр при изменениях законодательства/по решению Оператора; актуальная редакция — на сайте; вступает в силу с момента размещения, если не указано иное.',
  },
]
</script>
